import { Table, TableBody, TableHead, TableRow, useTheme } from "@mui/material";
import { PropTypes } from "./propType";
import InfiniteScroll from "react-infinite-scroll-component";
import { useEffect, useState } from "react";
import TableHeadCell from "./TableHeader";
import TableBodyCell from "./TableBodyCell";
import TableContainer from "./TableContainer";
import GetTableInputs from "./TableInput";
import { TableErrorMessage, TableLoader, TableDataLoader } from "./TableLoader";
import { TextContainer, TextWrapper } from "./TextContainer";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../redux/hooks";

const InfiniteTable = (props: PropTypes) => {
  const navigate = useNavigate();
  const {
    header,
    contentItems,
    fetchMoreData,
    hasMoreRecords,
    onInputChange,
    onHeaderAllCheckBoxChange,
    isInitialLoading = false,
    isError = false,
    filterCondition,
    metaData,
    totalRecords = 0,
    enableShowRecords = true,
    enableTotalRecords = true,
    isRandomAudit = false,
    editMode,
    lastKey,
  } = props;

  const theme = useTheme();
  const [headerSelection, setHeaderSelection] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const isContent: boolean = Boolean(contentItems && contentItems?.length > 10);
  const isMoreRecord: boolean = hasMoreRecords && isContent;
  useEffect(() => {
    if (contentItems && contentItems?.length > 0) {
      let validItems = contentItems;
      if (filterCondition)
        validItems = validItems?.filter((item) => filterCondition(item));
      const booleanValueToSet =
        validItems.length > 0 && selectedItems?.length === validItems?.length;
      setHeaderSelection(booleanValueToSet);
    }
  }, [selectedItems]);

  const userRole = useAppSelector(
    (state: any) => state.persistReducers.login.userRole
  );

  useEffect(() => {
    if (contentItems?.length === 0) setSelectedItems([]);
  }, [contentItems]);

  const onCheckboxChange = (item: any) => {
    const isExisting = selectedItems.includes(item);
    setSelectedItems((prev) => {
      if (isExisting) return prev.filter((currItem) => currItem !== item);
      else return [...prev, item];
    });
    if (onInputChange) onInputChange(item, !isExisting);
  };

  const onHeaderCheckBoxChange = () => {
    let selectedItemsToPass = [];
    if (headerSelection) {
      selectedItemsToPass = contentItems
        ? contentItems?.filter((item: any) => selectedItems.includes(item))
        : [];
      if (filterCondition)
        selectedItemsToPass = selectedItemsToPass.filter((item: any) =>
          filterCondition(item)
        );
      setSelectedItems([]);
    } else {
      let unselectedItems = contentItems
        ? contentItems?.filter((item: any) => !selectedItems.includes(item))
        : [];
      if (filterCondition)
        unselectedItems = unselectedItems.filter((item: any) =>
          filterCondition(item)
        );

      selectedItemsToPass = [...selectedItems, ...unselectedItems];
      setSelectedItems(selectedItemsToPass);
    }
    setHeaderSelection((prev) => !prev);
    if (onHeaderAllCheckBoxChange) {
      onHeaderAllCheckBoxChange(selectedItemsToPass, !headerSelection);
    } else if (onInputChange) {
      selectedItemsToPass.forEach((item: any) => {
        onInputChange(item, !headerSelection);
      });
    }
  };

  const handleEditClick = (item: any) => {
    if (editMode) {
      navigate("/adduseraccess", { state: { editMode: true, ...item } });
    }
  };

  const isDisabled = (item: any) => {
    if (filterCondition) return filterCondition(item);
    return true;
  };
  const renderHeader = (item: any) => {
    const { header, key } = item;
    return (
      <TableHeadCell key={key} tittle={header}>
        <GetTableInputs
          HeaderType={item}
          value={headerSelection}
          checked={headerSelection}
          onChange={onHeaderCheckBoxChange}
          {...item}
        />
      </TableHeadCell>
    );
  };

  const getRowItem = (item: any, header: any) => {
    return (
      <TableRow
        sx={{ padding: 0 }}
        onClick={() => {
          if (userRole === "Tenant Admin") handleEditClick(item);
        }}
      >
        {header.map((data: any, index: Number) => {
          return (
            <TableBodyCell key={data.key} tittle={item[data.key]}>
              <GetTableInputs
                tittle={item[data.key]}
                checked={selectedItems?.includes(item)}
                disabled={!isDisabled(item)}
                onChange={() => onCheckboxChange(item)}
                HeaderType={data}
                key={index}
                to={item.navigateTo}
                style={{
                  width: "100%",
                  display: "flex",
                  textTransform: "none",
                  textDecoration: "none",
                  justifyContent: "center",
                  alignItems: "center",
                  color: theme.color?.tableGrey,
                }}
                metaData={metaData}
              />
            </TableBodyCell>
          );
        })}
      </TableRow>
    );
  };

  function getMoreData() {
    if (fetchMoreData) fetchMoreData();
  }

  const getRecords = (countToDisplay: Number, isTotal?: boolean) => {
    return (
      <TextContainer>
        <TextWrapper>{isTotal ? "Total" : "Showing"} :</TextWrapper>
        <TextWrapper width={"60%"}>
          <span>
            {" "}
            <span style={{ color: "orange" }}>
              {" "}
              {countToDisplay.toString()}{" "}
            </span>{" "}
            records
          </span>
        </TextWrapper>
      </TextContainer>
    );
  };

  return (
    <>
      {enableTotalRecords && getRecords(totalRecords, true)}
      {enableShowRecords &&
        getRecords(contentItems?.length ? contentItems?.length : 0)}
      <TableContainer>
        <InfiniteScroll
          dataLength={contentItems ? contentItems.length : 0}
          next={getMoreData}
          hasMore={isMoreRecord}
          loader={
            isRandomAudit ? null : lastKey === null ? null : <TableDataLoader />
          }
          scrollableTarget="scrollable"
          style={{ overflow: "unset" }}
        >
          <Table stickyHeader>
            <TableHead
              sx={{
                backgroundColor: theme.color?.tableHeader,
              }}
            >
              <TableRow>{header?.map((item) => renderHeader(item))}</TableRow>
            </TableHead>
            <TableBody>
              {isInitialLoading ? (
                <TableLoader />
              ) : isError || contentItems?.length === 0 ? (
                <TableErrorMessage
                  isError={isError}
                  isNoContent={!isError && contentItems?.length === 0}
                />
              ) : (
                contentItems?.map((item: any) => getRowItem(item, header))
              )}
            </TableBody>
          </Table>
        </InfiniteScroll>
      </TableContainer>
    </>
  );
};

export default InfiniteTable;
